import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Box } from '@mui/material';
import { MainContainer } from '../../../components/ContentWrapper';
import config from '../../../config';
import { getAdminMetrics, postCategory } from '../../../api/endpoints';
import axiosInstant from '../../../api';
import { WhiteBackground } from '../../../components/WhiteBackground';
import { DotLottleLoader } from '../../../components/Loader';
import { getComparator, ICategory, IOrder } from './constants';
import { Header } from './Header';
import { CategoryForm } from './CategoryForm';
import { Container } from './Container';
import { useCommonParams } from '../../../hooks/useCommonParams';

const Categories = () => {
  const [data, setData] = useState<Array<ICategory>>([]);
  const { sortType, setSortType } = useCommonParams({ defaultSortKey: 'name', defaultSortAscending: true });
  const order: IOrder = sortType.isAscending ? 'asc' : 'desc';
  const orderBy = sortType.key as keyof ICategory;
  const [showFullLoader, setShowFullLoader] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ICategory>();
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const fetchData = async () => {
    const url = `${config.API_URL}${getAdminMetrics}`;

    try {
      const response = await axiosInstant.get(url);
      if (response.data.length) setData([...response.data]);
    } catch (err) {
      toast.error('Failed to fetch categories');
    } finally {
      setShowFullLoader(false);
      setIsLoading(false);
    }
  };

  const onSortChange = (
    updatedOrderBy: keyof ICategory,
    updatedOrder: IOrder
  ) => {
    setSortType({ key: updatedOrderBy, isAscending: updatedOrder === 'asc' });
  };

  const onClose = () => {
    setSelectedCategory(undefined);
    setShowCategoryForm(false);
  };

  const submitCategory = async (payload: Partial<ICategory>, id?: number) => {
    const url = `${config.API_URL}${postCategory}`;
    setIsLoading(true);

    try {
      const response = id
        ? await axiosInstant.patch(`${url}/${id}`, payload)
        : await axiosInstant.post(url, payload);
      if (response.data) fetchData();
      onClose();
    } catch (err) {
      toast.error('Failed to update category');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setShowFullLoader(true);
    fetchData();
  }, []);

  const sortedData = useMemo(
    () => [...data].sort(getComparator(orderBy, order)),
    [data, orderBy, order]
  );

  return (
    <WhiteBackground>
      <MainContainer>
        <Header onCreate={() => setShowCategoryForm(true)} />
        <Box className="relative">
          {showFullLoader && <DotLottleLoader />}
          {!showFullLoader && data.length > 0 && (
            <Container
              order={order}
              orderBy={orderBy}
              data={sortedData}
              handlers={{
                onSortChange,
                onPublish: (id, status) => submitCategory({ status }, id),
                onEdit: (e) => {
                  setSelectedCategory(e);
                  setShowCategoryForm(true);
                },
              }}
            />
          )}
          {isLoading && (
            // Scrim stays literal: bg-opacity-* cannot tint a var()-backed
            // color, so bg-surface would drop the 80% translucency.
            <Box className="flex items-center justify-center absolute w-full h-full inset-0 bg-white bg-opacity-80">
              <DotLottleLoader fill={false} />
            </Box>
          )}
        </Box>
      </MainContainer>
      {showCategoryForm && (
        <CategoryForm
          isLoading={isLoading}
          categoriesList={data}
          data={selectedCategory}
          onSubmit={(payload) => submitCategory(payload, selectedCategory?.id)}
          onClose={onClose}
        />
      )}
    </WhiteBackground>
  );
};

export default Categories;
